// Wachtwoorden van alle diensten aanpassen vanuit de OS (edge function wachtwoord-beheer).
// Bestaande wachtwoorden zijn niet op te vragen, alleen opnieuw in te stellen. Het wachtwoord wordt in de OS niet bewaard.
import { useEffect, useState } from "react";
import { supabase } from "./supabase";

const MUTED = "#a0a4af";
const GREEN = "#34bd82";
const RED = "#e5564e";
const MIN = 10;

function sterkWachtwoord() {
  const tekens = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const a = new Uint32Array(20);
  crypto.getRandomValues(a);
  return Array.from(a, (n) => tekens[n % tekens.length]).join("");
}

async function roep(body) {
  const { data, error } = await supabase.functions.invoke("wachtwoord-beheer", { body });
  if (error) {
    let melding = error.message;
    try {
      const j = await error.context?.json?.();
      if (j?.fout) melding = j.fout;
    } catch { /* geen JSON in het antwoord */ }
    throw new Error(melding);
  }
  if (data?.fout) throw new Error(data.fout);
  return data;
}

export default function WachtwoordBeheer() {
  const [diensten, setDiensten] = useState([]);
  const [dienst, setDienst] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [laden, setLaden] = useState(false);
  const [kies, setKies] = useState(null);
  const [pw, setPw] = useState("");
  const [toon, setToon] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const [gelukt, setGelukt] = useState("");
  const [log, setLog] = useState([]);

  const laadLog = () => roep({ actie: "log" }).then((d) => setLog(d.log || [])).catch(() => {});

  useEffect(() => {
    roep({ actie: "diensten" })
      .then((d) => {
        setDiensten(d.diensten || []);
        if (d.diensten?.length) setDienst(d.diensten[0].dienst);
      })
      .catch((e) => setFout(e.message));
    laadLog();
  }, []);

  useEffect(() => {
    if (!dienst) return;
    let weg = false;
    setLaden(true);
    setFout("");
    setKies(null);
    setPw("");
    setAccounts([]);
    roep({ actie: "accounts", dienst })
      .then((d) => { if (!weg) setAccounts(d.accounts || []); })
      .catch((e) => { if (!weg) setFout(e.message); })
      .finally(() => { if (!weg) setLaden(false); });
    return () => { weg = true; };
  }, [dienst]);

  const huidige = diensten.find((d) => d.dienst === dienst);
  const gekozen = accounts.find((a) => a.id === kies);
  const isSpel = huidige?.soort === "spel";

  const opslaan = async () => {
    if (!gekozen || pw.length < MIN) return;
    const wie = gekozen.email || gekozen.naam;
    const vraag = isSpel
      ? `De beheersleutel van ${huidige.label} wordt vervangen. Doorgaan?`
      : `Nieuw wachtwoord voor ${wie} (${huidige.label}) instellen? Hij wordt overal uitgelogd.`;
    if (!window.confirm(vraag)) return;
    setBezig(true);
    setFout("");
    setGelukt("");
    try {
      await roep({ actie: "zetten", dienst, id: gekozen.id, wachtwoord: pw });
      setGelukt(`Nieuw wachtwoord ingesteld voor ${wie}.`);
      setPw("");
      setKies(null);
      laadLog();
    } catch (e) {
      setFout(e.message);
      laadLog();
    } finally {
      setBezig(false);
    }
  };

  const kopieer = () => navigator.clipboard?.writeText(pw).then(() => setGelukt("Gekopieerd naar het klembord.")).catch(() => {});

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>Wachtwoorden</h2>
        <p style={{ color: MUTED, fontSize: 12, marginTop: 2 }}>
          Stel per dienst een nieuw wachtwoord in. Bestaande wachtwoorden zijn niet te bekijken, alleen opnieuw in te stellen. Het wachtwoord wordt hier niet bewaard.
        </p>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {diensten.map((d) => (
          <span key={d.dienst} className={"tab" + (dienst === d.dienst ? " on" : "")} onClick={() => setDienst(d.dienst)}>{d.label}</span>
        ))}
      </div>

      {fout && <div className="card" style={{ color: RED, fontSize: 13, marginBottom: 12 }}>{fout}</div>}
      {gelukt && <div className="card" style={{ color: GREEN, fontSize: 13, marginBottom: 12 }}>{gelukt}</div>}

      <div className="card" style={{ marginBottom: 14 }}>
        <p className="lbl" style={{ marginBottom: 10 }}>{huidige ? `Accounts van ${huidige.label}` : "Accounts"}</p>
        {laden && <p style={{ color: MUTED, fontSize: 13 }}>Laden...</p>}
        {!laden && accounts.length === 0 && !fout && <p style={{ color: MUTED, fontSize: 13 }}>Geen accounts gevonden.</p>}
        {accounts.map((a) => (
          <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 0", borderTop: "1px solid #2a2c34" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>{a.email || a.naam}</div>
              <div style={{ fontSize: 11, color: MUTED }}>{[a.email ? a.naam : null, a.rol].filter(Boolean).join(" · ")}</div>
            </div>
            <button className="btn bg" style={{ fontSize: 11 }} onClick={() => { setKies(a.id); setPw(""); setGelukt(""); setFout(""); }}>
              {isSpel ? "Sleutel vervangen" : "Wachtwoord zetten"}
            </button>
          </div>
        ))}
      </div>

      {gekozen && (
        <div className="card" style={{ marginBottom: 14 }}>
          <p className="lbl" style={{ marginBottom: 10 }}>Nieuw wachtwoord voor {gekozen.email || gekozen.naam}</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input
              type={toon ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder={`Minstens ${MIN} tekens`}
              autoComplete="new-password"
              style={{ flex: "1 1 260px", minWidth: 200 }}
            />
            <button className="btn bg" style={{ fontSize: 11 }} onClick={() => { setPw(sterkWachtwoord()); setToon(true); }}>Genereer sterk wachtwoord</button>
            <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setToon((v) => !v)}>{toon ? "Verberg" : "Toon"}</button>
            <button className="btn bg" style={{ fontSize: 11 }} disabled={!pw} onClick={kopieer}>Kopieer</button>
          </div>
          <p style={{ color: MUTED, fontSize: 11, marginTop: 8 }}>
            {isSpel
              ? "Dit wordt de nieuwe beheersleutel van het spel. De OS onthoudt hem zodat het spelbeheer blijft werken."
              : "Kopieer het wachtwoord voor je opslaat: daarna is het niet meer te zien. Het account wordt op alle apparaten uitgelogd."}
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button className="btn bp" disabled={bezig || pw.length < MIN} onClick={opslaan}>{bezig ? "Bezig..." : "Wachtwoord instellen"}</button>
            <button className="btn bg" disabled={bezig} onClick={() => { setKies(null); setPw(""); }}>Annuleren</button>
          </div>
        </div>
      )}

      <div className="card">
        <p className="lbl" style={{ marginBottom: 10 }}>Laatste wijzigingen</p>
        {log.length === 0 && <p style={{ color: MUTED, fontSize: 13 }}>Nog niets gewijzigd.</p>}
        {log.map((r, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12, padding: "6px 0", borderTop: i ? "1px solid #2a2c34" : "none" }}>
            <span>
              <span style={{ color: r.gelukt ? GREEN : RED }}>{r.gelukt ? "✓" : "✗"}</span> {r.dienst} · {r.account}
              {!r.gelukt && r.melding ? <span style={{ color: MUTED }}> ({r.melding})</span> : null}
            </span>
            <span style={{ color: MUTED, whiteSpace: "nowrap" }}>{new Date(r.tijdstip).toLocaleString("nl-NL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
